import axios from 'axios';
import { URLEndpoints } from '../../constants/urlConstants';
import { filterActiveInvoices } from '../../utils/invoiceVisibility';
const BASE_URL = process.env.REACT_APP_NODEJS_SERVER_URL

export const fetchFinanceInvoices = async ({ instanceUniqueId, view = "invoices", page = 1, rpp = "all", sort = "invoiceDate", dir = "DESC", dateFrom, dateTo } = {}) => {
  const res = await axios.get(`${BASE_URL}/${URLEndpoints.invoices}/all`, {
    headers: { accessToken: localStorage.getItem("accessToken") },
    params: { instanceUniqueId, view, page, rpp, sort, dir, ...(dateFrom ? { dateFrom } : {}), ...(dateTo ? { dateTo } : {}) },
  });
  return Array.isArray(res?.data?.invoices) ? res.data.invoices : [];
};

// ---- NEW: invoice summary helpers (add below your existing exports) ----

/**
 * Tiny safe number helper.
 */
const _toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  
  /**
   * Safe getter across several likely field names.
   */
const _pick = (obj, keys, fallback = undefined) => {
    for (const k of keys) {
      if (obj && obj[k] != null) return obj[k];
    }
    return fallback;
  };

export const isInvoicePaidStatus = (invoice = {}) => {
    const paymentStatus = String(invoice?.paymentStatus || '').trim().toLowerCase();
    const invoiceStatus = String(invoice?.invoiceStatus || '').trim().toLowerCase();
    return ['paid', 'completed', 'succeeded', 'success'].includes(paymentStatus)
      || ['paid', 'completed', 'succeeded', 'success'].includes(invoiceStatus);
  };
  
  /**
   * Fetch a simple rollup for an order's invoices.
   * Returns: { totalInvoiced, paid, open, count, currency? }
   *
   * - `totalInvoiced`: sum of invoice totals
   * - `paid`: sum of totals for invoices considered paid
   * - `open`: outstanding; prefers explicit balance/outstanding, else assumes full amount open
   */
  export const fetchInvoicesSummary = async (orderId) => {
    // Reuse your existing list call
    const data = await fetchInvoicesListByOrder(orderId);
  
    // Shape can be { invoices: [...] } or just [...]
    const invoices = Array.isArray(data?.invoices)
      ? data.invoices
      : (Array.isArray(data) ? data : []);
    const activeInvoices = filterActiveInvoices(invoices);
  
    let totalInvoiced = 0;
    let paid = 0;
    let open = 0;
  
    // Optional currency sniff (best-effort)
    let currency = null;
  
    for (const inv of activeInvoices) {
      // common amount field variants
      const amt = _toNumber(_pick(inv, ["amount", "total", "totalAmount", "grandTotal"], 0));
  
      // explicit balance/outstanding if present
      const balanceRaw = _pick(inv, ["balance", "outstanding", "amountDue", "due"], null);
      const balance = balanceRaw != null ? _toNumber(balanceRaw) : null;
  
      const status = String(_pick(inv, ["paymentStatus", "invoiceStatus", "status"], "")).toLowerCase();
  
      if (!currency) {
        currency = _pick(inv, ["currency", "currencyCode"], null);
      }
  
      totalInvoiced += amt;
  
      const isPaid =
        status.includes("paid") ||
        status.includes("complete") ||
        status.includes("completed") ||
        (balance !== null && balance <= 0);
  
      if (isPaid) {
        paid += amt;
      } else {
        // Prefer explicit balance; otherwise assume full amount is still open
        open += (balance !== null ? Math.max(0, balance) : Math.max(0, amt));
      }
    }
  
    return { totalInvoiced, paid, open, count: activeInvoices.length, ...(currency ? { currency } : {}) };
  };
  
  /**
   * Convenience helpers if you want quick reads elsewhere.
   */
  export const fetchInvoicedTotal = async (orderId) => {
    const s = await fetchInvoicesSummary(orderId);
    return s.totalInvoiced || 0;
  };
  
  export const fetchOpenInvoiceBalance = async (orderId) => {
    const s = await fetchInvoicesSummary(orderId);
    return s.open || 0;
  };

  // Fetch invoices by project


export const fetchPublicInvoicePaymentContext = async ({ contextType, salesOrderId, orderId, paymentToken } = {}) => {
    if (contextType !== 'generated-sales-order') {
      throw new Error('Unsupported public invoice payment context.');
    }
    const safeSalesOrderId = String(salesOrderId || '').trim();
    const safeOrderId = String(orderId || '').trim();
    if (!safeSalesOrderId || !safeOrderId) return null;

    const params = new URLSearchParams({ orderId: safeOrderId });
    if (paymentToken) params.set('prt', paymentToken);

    const res = await axios.get(
      `${BASE_URL}/${URLEndpoints.invoices}/public/payment-context/generated-sales-order/${encodeURIComponent(safeSalesOrderId)}?${params.toString()}`
    );
    return res?.data || null;
  };

// Structured sends for every invoice (including voided history) on one project.
// Read-only. This is not the customer's generic email-notes history.
export const fetchInvoiceEmailHistoryByOrder = async (orderId, { instanceUniqueId } = {}) => {
  const response = await axios.get(
    `${BASE_URL}/${URLEndpoints.invoices}/order/${encodeURIComponent(orderId)}/email-history`,
    {
      headers: { accessToken: localStorage.getItem('accessToken') },
      params: { instanceUniqueId },
    }
  );
  return response?.data || { events: [], total: 0 };
};

export const fetchInvoicesListByOrder = async (id, { throwOnError = false } = {}) => {
    try {
      const headers = { accessToken: localStorage.getItem("accessToken") };
      const res = await axios.get(
        `${BASE_URL}/${URLEndpoints.invoices}/byOrder/${id}/`,
        { headers }
      );
      const payload = res?.data;

      // The Sales Order precheck must not interpret a malformed response as
      // proof that there are no existing invoices. Other consumers retain the
      // legacy lenient behavior unless they opt into strict verification.
      const isInvoiceListResponse = Array.isArray(payload) ||
        Array.isArray(payload?.data) || Array.isArray(payload?.invoices);
      if (throwOnError && !isInvoiceListResponse) {
        throw new Error('Invoice history response could not be verified.');
      }

      // Normalize to a plain array regardless of server shape
      const list =
        Array.isArray(payload) ? payload :
        Array.isArray(payload?.data) ? payload.data :
        Array.isArray(payload?.invoices) ? payload.invoices :
        [];
  
      return list;
    } catch (error) {
      if (throwOnError) throw error;
      console.log(error);
      return []; // be lenient for existing consumers; strict reads opt in
    }
  };


// Informational safeguard only. These calls never create or block an invoice.
export const fetchInvoiceAddressPrecheck = async (orderId, { instanceUniqueId, salesOrderId } = {}) => {
  const response = await axios.get(
    `${BASE_URL}/${URLEndpoints.invoices}/order/${encodeURIComponent(orderId)}/address-precheck`,
    {
      headers: { accessToken: localStorage.getItem('accessToken') },
      params: { instanceUniqueId, ...(salesOrderId ? { salesOrderId } : {}) },
    }
  );
  return response?.data?.precheck || null;
};

export const notifyInvoiceAddressMissing = async (orderId, { instanceUniqueId, salesOrderId } = {}) => {
  const response = await axios.post(
    `${BASE_URL}/${URLEndpoints.invoices}/order/${encodeURIComponent(orderId)}/address-precheck/notify`,
    { instanceUniqueId, ...(salesOrderId ? { salesOrderId } : {}) },
    { headers: { accessToken: localStorage.getItem('accessToken') } }
  );
  return response?.data || null;
};

export const createInvoice = async (
  orderId,
  {
    instanceUniqueId,
    salesOrderId = null,
    role = "full",
    sendEmailNow = false,
    email = null,
    invoiceMode = "customer_selection",
    invoiceAmount = null,
    deriveTermsTaxesFrom = null,
    invoicingCartKey = null,
    qboQueueOnCreate = undefined,
    preparationRevision = null,
    preparationToken = null,
    manualAdditionalInvoice = false,
    manualAdditionalReason = '',
    confirmAdditionalInvoice = false,
  } = {}
) => {
    const headers = { accessToken: localStorage.getItem("accessToken") };
    return axios.post(
      `${BASE_URL}/${URLEndpoints.invoices}/${orderId}`,
      { instanceUniqueId, salesOrderId, invoiceRole: role, sendEmailNow, email, invoiceMode, invoiceAmount, deriveTermsTaxesFrom, invoicingCartKey, qboQueueOnCreate, preparationRevision, preparationToken, ...(manualAdditionalInvoice ? { manualAdditionalInvoice, manualAdditionalReason, confirmAdditionalInvoice } : {}) },
      { headers }
  );
};

export const prepareInvoice = async (orderId, payload = {}) => {
  const headers = { accessToken: localStorage.getItem("accessToken") };
  const response = await axios.post(
    `${BASE_URL}/${URLEndpoints.invoices}/${orderId}/prepare`,
    payload,
    { headers }
  );
  return response?.data?.review;
};

export const resolveInvoiceCreationPreparation = async ({
  reviewEnabled,
  preparedReview,
  orderId,
  request,
}) => {
  if (reviewEnabled) {
    // A review is a read-only Sales Order preview. The invoice-owned cart
    // does not exist until createInvoice succeeds.
    if (!preparedReview?.preparation?.preparationToken) {
      throw new Error('Review the prepared invoice before creating it.');
    }
    return preparedReview;
  }

  return prepareInvoice(orderId, request);
};

// Admin-only: undo the latest eligible non-deposit invoice created by testing.
export const previewResetInvoiced = async ({ orderId, instanceUniqueId }) => {
  const response = await axios.get(
    `${BASE_URL}/${URLEndpoints.invoices}/order/${encodeURIComponent(orderId)}/reset-invoiced/preview`,
    { headers: { accessToken: localStorage.getItem('accessToken') }, params: { instanceUniqueId } }
  );
  return response.data;
};

export const confirmResetInvoiced = async ({ orderId, instanceUniqueId, invoiceId }) => {
  const response = await axios.post(
    `${BASE_URL}/${URLEndpoints.invoices}/order/${encodeURIComponent(orderId)}/reset-invoiced`,
    { instanceUniqueId, invoiceId },
    { headers: { accessToken: localStorage.getItem('accessToken') } }
  );
  return response.data;
};

export const fetchOrderBalanceStatus = async (orderId) => {
    try {
        const res = await axios.get(
          `${BASE_URL}/${URLEndpoints.invoices}/order/${orderId}/balance-status`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        )
        return res?.data
    } catch (error) {
        return Promise.reject(error)
    }
}

export const getPublicInvoice = async (id) => {
  try {
    const res = await axios.get(
      `${BASE_URL}/${URLEndpoints.invoices}/public/${id}`
    );

    return res?.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getSingleinvoice = async (id) => {
    try {
        const res = await axios.get(
          `${BASE_URL}/${URLEndpoints.invoices}/${id}/`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        )
        return res?.data
    } catch (error) {
        return Promise.reject(error)
    }
}


export const updateInvoice = async (id,payload) => {
    try {
        const res = await axios.put(
          `${BASE_URL}/${URLEndpoints.invoices}/${id}/`,
          payload,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        )
        return res?.data
    } catch (error) {
        return Promise.reject(error)
    }
}

export const resendInvoiceEmail = async (id, payload = {}) => {
    try {
        const res = await axios.post(
          `${BASE_URL}/${URLEndpoints.invoices}/${id}/resend-email`,
          payload,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
        return res?.data;
    } catch (error) {
        return Promise.reject(error)
    }
}

export const fetchBillingFinancialSummary = async (orderId) => {
  const res = await axios.get(
    `${BASE_URL}/${URLEndpoints.invoices}/byOrder/${orderId}/billing-financial-summary`,
    { headers: { accessToken: localStorage.getItem("accessToken") } }
  );
  return res?.data || null;
};
